import {
  EXPENSE_CATEGORIES,
  EXPENSE_CATEGORY_LABELS,
  EXPENSE_PAYMENT_METHODS,
  SALE_PAYMENT_METHODS,
  STOCK_ITEM_TYPES,
  isNonStockExpenseCategory,
} from "@artisancode/api-types";
import { z } from "zod";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export const expenseItemSchema = z.object({
  description: z.string().min(1, "Nama item wajib diisi"),
  itemType: z.enum(STOCK_ITEM_TYPES).nullish(),
  itemId: z.string().nullish(),
  uomId: z.string().nullish(),
  qty: z.number().positive("Qty minimal > 0"),
  qtyInUom: z.number().positive().nullish(),
  pricePerUnit: z.number().min(0, "Harga minimal 0"),
});

export type ExpenseItemFormValues = z.infer<typeof expenseItemSchema>;

export const emptyExpenseItem: ExpenseItemFormValues = {
  description: "",
  itemType: null,
  itemId: null,
  uomId: null,
  qty: 1,
  qtyInUom: null,
  pricePerUnit: 0,
};

export const expenseSchema = z
  .object({
    date: z.string().min(1, "Tanggal wajib diisi"),
    category: z.enum(EXPENSE_CATEGORIES),
    description: z.string().optional(),
    items: z.array(expenseItemSchema).min(1, "Minimal satu item pengeluaran"),
    qty: z.number().int().min(1).optional(),
    pricePerUnit: z.number().min(0).optional(),
    discountAmount: z.number().min(0, "Diskon tidak boleh negatif"),
    taxAmount: z.number().min(0, "Pajak tidak boleh negatif"),
    paymentMethod: z.enum(SALE_PAYMENT_METHODS),
    fundingSource: z.enum(EXPENSE_PAYMENT_METHODS),
    referenceNote: z.string().optional(),
    receiptKey: z.string().nullable().optional(),
    receiptKeys: z.array(z.string()).optional(),
    workspaceId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (isNonStockExpenseCategory(data.category)) {
      const hasStockItem = data.items.some(
        (i) => i.itemType !== null && i.itemType !== undefined,
      );
      if (hasStockItem) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["category"],
          message: `Kategori belanja '${EXPENSE_CATEGORY_LABELS[data.category] || data.category}' tidak boleh memiliki baris stok (bahan baku/produk).`,
        });
      }
    }
    if (data.category === "raw_material") {
      const hasRawMaterial = data.items.some(
        (i) => i.itemType === "raw_material",
      );
      if (!hasRawMaterial) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["category"],
          message:
            "Kategori belanja 'Bahan Baku' wajib memiliki minimal satu baris bahan baku.",
        });
      }
    }
  });

export type ExpenseFormValues = z.infer<typeof expenseSchema>;

export const defaultExpenseValues: ExpenseFormValues = {
  date: todayIso(),
  category: "operational",
  description: "",
  items: [emptyExpenseItem],
  qty: 1,
  pricePerUnit: 0,
  discountAmount: 0,
  taxAmount: 0,
  paymentMethod: "cash",
  fundingSource: "cash",
  referenceNote: "",
  receiptKey: null,
  receiptKeys: [],
  workspaceId: "",
};
